import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const COOKIE_KEY = 'taxera_cookie_consent';

const CookieConsent = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    functional: true,
  });

  const categories = [
    { id: 'necessary', label: t('cookie.essential'), desc: t('cookie.essentialDesc'), required: true },
    { id: 'analytics', label: t('cookie.analytics'), desc: t('cookie.analyticsDesc'), required: false },
    { id: 'marketing', label: t('cookie.marketing'), desc: t('cookie.marketingDesc'), required: false },
    { id: 'functional', label: t('common.settings', 'Functional'), desc: 'Enable enhanced functionality and personalisation.', required: false },
  ];

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_KEY);
    if (!stored) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (prefs) => {
    localStorage.setItem(COOKIE_KEY, JSON.stringify({ ...prefs, timestamp: new Date().toISOString() }));
    setVisible(false);
  };

  const acceptAll = () => saveConsent({ necessary: true, analytics: true, marketing: true, functional: true });
  const rejectOptional = () => saveConsent({ necessary: true, analytics: false, marketing: false, functional: false });
  const saveCustom = () => saveConsent(preferences);

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-0 left-0 right-0 z-[60] p-4 lg:p-6"
        data-testid="cookie-consent"
      >
        <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
          <div className="p-5 lg:p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-taxera-teal/10 flex items-center justify-center shrink-0">
                <Cookie className="w-5 h-5 text-taxera-teal" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-base font-bold text-taxera-charcoal mb-1">{t('cookie.title')}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">
                  {t('cookie.description')}{' '}
                  <a href="/privacy" className="text-taxera-blue hover:underline">{t('footer.privacy')}</a>.
                </p>
              </div>
              <button onClick={() => setVisible(false)} className="text-slate-400 hover:text-slate-600 shrink-0">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-4">
              <button
                onClick={acceptAll}
                className="px-5 py-2.5 taxera-gradient text-white rounded-full text-sm font-semibold hover:shadow-lg transition-all"
                data-testid="cookie-accept-all"
              >
                {t('cookie.acceptAll')}
              </button>
              <button
                onClick={rejectOptional}
                className="px-5 py-2.5 bg-white border border-slate-200 text-taxera-charcoal rounded-full text-sm font-semibold hover:border-slate-300 transition-all"
                data-testid="cookie-reject"
              >
                {t('cookie.rejectOptional')}
              </button>
              <button
                onClick={() => setShowPreferences(!showPreferences)}
                className="inline-flex items-center gap-1 px-4 py-2.5 text-sm font-semibold text-slate-500 hover:text-taxera-charcoal transition-colors"
                data-testid="cookie-customize"
              >
                {t('cookie.customise')}
                {showPreferences ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>

          <AnimatePresence>
            {showPreferences && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="px-5 lg:px-6 pb-5 pt-3 border-t border-slate-100">
                  <div className="space-y-3">
                    {categories.map((cat) => (
                      <div key={cat.id} className="flex items-center justify-between py-2">
                        <div>
                          <div className="text-sm font-semibold text-taxera-charcoal">{cat.label}</div>
                          <div className="text-xs text-slate-400">{cat.desc}</div>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer shrink-0 ml-4">
                          <input
                            type="checkbox"
                            checked={preferences[cat.id]}
                            onChange={(e) => !cat.required && setPreferences(p => ({ ...p, [cat.id]: e.target.checked }))}
                            disabled={cat.required}
                            className="sr-only peer"
                            data-testid={`cookie-toggle-${cat.id}`}
                          />
                          <div className={`w-9 h-5 rounded-full transition-colors ${cat.required ? 'bg-taxera-teal/50' : 'bg-slate-200 peer-checked:bg-taxera-teal'}`}>
                            <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${preferences[cat.id] ? 'translate-x-4' : ''}`} />
                          </div>
                        </label>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={saveCustom}
                    className="mt-3 px-5 py-2 bg-taxera-charcoal text-white rounded-full text-sm font-semibold hover:bg-taxera-charcoal/90 transition-all"
                    data-testid="cookie-save-preferences"
                  >
                    {t('cookie.save')}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieConsent;
